import React from 'react';
import * as Linking from 'expo-linking';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import useNotes from '../../../hooks/api/useNotes';
import urls from '../../../utils/urls';
import Icon, { IconName } from '../../v2BaseComponents/Icon';
import { View } from 'react-native';
import { colors } from '../../../utils/theme';
import IconButton from '../../v2BaseComponents/IconButton';
import FullScreenImageView from '../../v2BaseComponents/FullScreenImageView/FullScreenImageView';
import { UnstyledAvatar } from '../../v2BaseComponents/Avatar';
import Text from '../../v2BaseComponents/Text';
import PressableDynamicOpacity from '../../v2BaseComponents/PressableDynamicOpacity';
import { APITypes } from '../../../primary-record-types';
import MedicalProfileFileIconDisplay from '../MedicalProfileFileIconDisplay';
import { useNestedNavigation } from '../../../utils/appNavigationNames';
import { truncate } from 'lodash';
import { AvatarAPIImagesProvider } from '../../v2BaseComponents/FullScreenImageView';

export type TempImageFileProps = {
    type: 'temp-image';
    tempFileId: string;
};

export type NoteImageFileProps = {
    type: 'image';
    fileId: string;
    noteId: string;
};

export type TempFileFileProps = {
    type: 'temp-file';
    tempFileId: string;
};

export type NoteFileFileProps = {
    type: 'file';
    fileId: string;
    noteId: string;
};

export type FileProps = TempImageFileProps | NoteImageFileProps | TempFileFileProps | NoteFileFileProps;

export type AttachmentListItemOwnProps = {
    description: string;
    file: FileProps;
    onDelete?: () => void;
    medicalProfileFile: APITypes.medicalProfileFilesService.MedicalProfileFileListItem | null;
    icon?: IconName;
};

export type AttachmentListItemProps = AttachmentListItemOwnProps;

const displaySize = {
    width: 91,
    height: 56,
};

const truncationLength = 50;

const AttachmentListItem: React.FC<AttachmentListItemProps> = (props) => {
    const { nestedProfileNavigation } = useNestedNavigation();

    const { isLoadingNoteFile, loadNoteFile } = useNotes({
        onLoadNoteFile: (url) => {
            Linking.openURL(url);
        },
    });

    const getImageUrl = (fileProps: TempImageFileProps | NoteImageFileProps) =>
        fileProps.type === 'temp-image'
            ? urls.api.files.tempFile(fileProps.tempFileId)
            : urls.api.notes.noteImage(fileProps.noteId, fileProps.fileId);

    const renderDisplay = () => {
        if (props.medicalProfileFile) {
            return (
                <MedicalProfileFileIconDisplay
                    file={props.medicalProfileFile}
                    width={displaySize.width}
                    height={displaySize.height}
                />
            );
        }

        if (props.file.type === 'temp-image' || props.file.type === 'image') {
            return (
                <View testID="attachment-list-item-attachment-image">
                    <UnstyledAvatar
                        alt="Attachment"
                        avatarUrl={getImageUrl(props.file)}
                        width={displaySize.width}
                        height={displaySize.height}
                        resizeMode="cover"
                    />
                </View>
            );
        } else {
            return (
                <View testID="attachment-list-item-attachment-file">
                    <Icon name={props.icon || 'FileText'} size="button-icon" />
                </View>
            );
        }
    };

    const renderWithPress = (onPress: () => void, isDisabled: boolean) => (
        <Stack horizontal fullWidth align="center" justify="space-between" testID="attachment-list-item">
            <Stack horizontal justify="flex-start" align="center" space={contentStackSpacing} flex={1}>
                <PressableDynamicOpacity onPress={onPress} isDisabled={isDisabled}>
                    <View
                        style={{
                            width: displaySize.width,
                            height: displaySize.height,
                            backgroundColor: colors['Grey-Light2'],
                            borderRadius: 5,
                            borderColor: colors['Dividers'],
                            overflow: 'hidden',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        {renderDisplay()}
                    </View>
                </PressableDynamicOpacity>
                <View style={{ flex: 1 }}>
                    <Text variant="body-1" wrapText>
                        {truncate(props.description, { length: truncationLength })}
                    </Text>
                </View>
            </Stack>
            {props.onDelete ? (
                <IconButton
                    variant="invert"
                    icon="X"
                    onPress={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.onDelete?.();
                    }}
                    testID="attachment-list-item-delete-button"
                />
            ) : null}
        </Stack>
    );

    if (props.medicalProfileFile !== null) {
        return renderWithPress(() => {
            nestedProfileNavigation('file', {
                fileId: props.medicalProfileFile!.medicalProfileFileId,
                profileId: props.medicalProfileFile!.medicalProfileId,
            });
        }, false);
    }

    if (props.file.type === 'temp-image' || props.file.type === 'image') {
        return (
            <AvatarAPIImagesProvider
                images={[
                    {
                        imageViewImageUrl: getImageUrl(props.file),
                        imageViewImageDescription: props.description,
                    },
                ]}
            >
                {(renderImagesData) => (
                    <FullScreenImageView
                        renderImagesData={renderImagesData}
                        renderImages={(images, onPress, getIsPressEnabled) =>
                            renderWithPress(() => onPress(images[0]), !getIsPressEnabled(images[0]))
                        }
                    />
                )}
            </AvatarAPIImagesProvider>
        );
    } else if (props.file.type === 'temp-file' || props.file.type === 'file') {
        return renderWithPress(() => {
            if (props.file.type === 'temp-file') {
                loadNoteFile({ tempFileId: props.file.tempFileId });
            } else if (props.file.type === 'file') {
                loadNoteFile({ fileId: props.file.fileId, noteId: props.file.noteId });
            }
        }, isLoadingNoteFile);
    } else {
        return null;
    }
};

export default React.memo(AttachmentListItem);
